// ПРАВИЛО УРОВНЕЙ ОКНА КАБИНЕТА В БРАУЗЕРЕ (ТЗ WEB-PARITY-3, К3.1, владелец 02.10.2026).
// «Закрыть» поверхности (useSlidePanel().close) в браузере ведёт себя как в iOS-приложении:
// в окне открыто больше одного уровня — снимается ОДИН верхний, уровень один — закрывается
// окно. Раньше «закрыть» снимало окно целиком со всеми уровнями: выбор триггера в мастере
// закрывал весь мастер, «Готово» окна «Сообщение» — всю форму рассылки.
//
// Чистая часть правила живёт здесь (тест — drawer-levels.test.ts), DOM и историю ведёт
// components/cabinet/drawer.tsx.

/** Уровень окна, как его видит правило: достаточно ключа. */
export interface LevelRef {
  key: number;
}

/**
 * ЖИВЫЕ уровни: без тех, что уже уезжают. Снятый уровень остаётся в стопке до конца своего
 * выезда (его тело ещё на экране), но для счёта его уже нет — иначе два «закрыть» подряд
 * снимали бы один и тот же уровень дважды, а не два уровня.
 */
export function liveLevels<T extends LevelRef>(levels: readonly T[], leaving: ReadonlySet<number>): T[] {
  return levels.filter((l) => !leaving.has(l.key));
}

export type CloseStep<T extends LevelRef> = { kind: 'window' } | { kind: 'level'; level: T };

/** Что снимает «закрыть» поверхности: один верхний живой уровень или окно целиком. */
export function closeStep<T extends LevelRef>(levels: readonly T[], leaving: ReadonlySet<number>): CloseStep<T> {
  const live = liveLevels(levels, leaving);
  if (live.length <= 1) return { kind: 'window' };
  return { kind: 'level', level: live[live.length - 1]! };
}

/** Уровень, как его видят правила ниже: вид слоя из payload. */
export interface LevelView extends LevelRef {
  p: { sheet?: boolean; sheetDetent?: number; inSheet?: boolean };
}

/**
 * ЖЕСТ УРОВНЯ (владелец 09.10.2026, центр уведомлений на телефоне в браузере): лист уходит
 * свайпом вниз, горизонтальный слой — свайпом вправо. Страница ВНУТРИ листа (`inSheet`,
 * уведомление из ленты «Полученные») — горизонтальная, хоть и носит вид листа: её снимает
 * свайп вправо, как родной push внутри шторки iOS.
 */
export function levelDrag(p: LevelView['p'], nested: boolean): 'down' | 'side' {
  if (p.sheet && !(nested && p.inSheet)) return 'down';
  return 'side';
}

/**
 * ТАП МИМО НИЗКОГО ЛИСТА (владелец 09.10.2026, «Уведомления» → группы на телефоне в браузере):
 * пока сверху стоит вложенный низкий лист (`sheet` + `sheetDetent`), тап по уровню ПОД ним
 * (строка другой группы) не нажимает эту строку, а только закрывает лист. `hitKey` — ключ
 * уровня, по которому пришёлся тап (null — мимо всех уровней, по подложке).
 * 'close' — погасить тап и снять верхний лист; 'pass' — тап идёт своим путём.
 */
export function outsideTap<T extends LevelView>(
  levels: readonly T[],
  leaving: ReadonlySet<number>,
  hitKey: number | null,
  phone: boolean,
): 'close' | 'pass' {
  if (!phone || hitKey === null) return 'pass';
  const live = liveLevels(levels, leaving);
  if (live.length < 2) return 'pass';
  const top = live[live.length - 1]!;
  if (!top.p.sheet || !top.p.sheetDetent) return 'pass';
  return hitKey === top.key ? 'pass' : 'close';
}
